import React, { useCallback } from 'react';
import {
	ContextTokenType,
	type CustomField,
	type CustomFieldExtensionData,
	type CustomFieldExtensionPayload,
	type CustomFieldType,
} from '@helpcenter/forge-ui-utils';
import { ExtensionPointProvider } from '@helpcenter/forge-ui-utils/src/extension-point-provider';
import { ExtensionProvider } from '@helpcenter/forge-ui-utils/src/extension-provider';
import { Renderer } from '@helpcenter/forge-ui-utils/src/renderer';
import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import SectionMessage from '@atlaskit/section-message/message';
import type { ComponentMap } from '@atlassian/forge-ui/renderer/util';
import messages from './messages';

export interface Props {
	extension: CustomField | CustomFieldType;
	extensionData: CustomFieldExtensionData;
	extensionPayload: CustomFieldExtensionPayload;
	loadingComponent: React.ReactNode;
}

export const ForgeCustomFieldView = ({
	extension,
	extensionData,
	extensionPayload,
	loadingComponent,
}: Props) => {
	di(ExtensionPointProvider, ExtensionProvider, Renderer);
	const { formatMessage } = useIntl();

	const getComponents = useCallback(
		(defaults: ComponentMap): ComponentMap => {
			return {
				...defaults,
				Frame: () => (
					<SectionMessage
						title={formatMessage(messages.unsupportedFrameComponentTitle)}
						appearance="error"
					>
						{formatMessage(messages.unsupportedFrameComponentDescription)}
					</SectionMessage>
				),
			};
		},
		[formatMessage],
	);

	return (
		<ExtensionPointProvider extensionType={extension.type}>
			<ExtensionProvider extension={extension}>
				<Renderer
					extension={extension}
					extensionData={extensionData}
					extensionPayload={extensionPayload}
					entryPoint="view"
					loadingComponent={loadingComponent as JSX.Element}
					contextTokenType={ContextTokenType.JIRA}
					getComponents={getComponents}
				/>
			</ExtensionProvider>
		</ExtensionPointProvider>
	);
};
